/** 成长记录：列表的一行、详情（大卡面、谁发的、可以改故事 / 日期 / 照片），与 iOS CardRecordViews 一致 */
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { type Card, type ChildCard, run, useCalendar, useStore, writeEntity } from "../../core";
import { FormCard, ModalHeader, RowDivider, Sheet, SheetBody, TextArea, ValueRow } from "../../ui";
import { rarityStyle } from "../../ui/styles";
import { CardArtImage, CardPhoto, CardPhotosEditor } from "./CardPhotos";
import "./cards.css";

export function CardRecordRow({ record, card, onOpen }: { record: ChildCard; card: Card; onOpen: () => void }) {
  const cal = useCalendar();
  const r = rarityStyle(card.rarity);
  const photos = record.photoAssetIds ?? [];
  const meta = [record.occurredOn ?? cal.dateString(record.obtainedAt), record.actorName ? `${record.actorName}发的` : null, record.points ? `+${record.points} 分` : null].filter(Boolean).join(" · ");
  return (
    <button type="button" className="kp-record-row" onClick={onOpen} data-testid="card-record">
      <span className="kp-record-glyph" style={{ background: r.tint, color: r.ink }}>
        {card.imageAssetId ? <CardArtImage assetId={card.imageAssetId} /> : card.glyph}
      </span>
      <span className="kp-record-main">
        <span className="kp-record-title">
          <strong>{card.name}</strong>
          <em style={{ background: r.ink }}>{r.name}</em>
        </span>
        <small>{meta}</small>
        {record.story && <span className="kp-record-story">{record.story}</span>}
        {photos.length > 0 && (
          <span className="kp-record-photos">
            {photos.slice(0, 4).map((id) => (
              <CardPhoto key={id} assetId={id} size={44} />
            ))}
            {photos.length > 4 && <small className="kp-num">+{photos.length - 4}</small>}
          </span>
        )}
      </span>
      <ChevronRight size={15} strokeWidth={2.6} className="kp-record-chevron" />
    </button>
  );
}

/** 详情页的大卡面：占满一行 */
export function BigCard({ card, subtitle }: { card: Card; subtitle: string }) {
  const r = rarityStyle(card.rarity);
  return (
    <div className="kp-big-card" style={{ borderColor: r.ink }}>
      <div className={`kp-big-card-art${card.imageAssetId ? " has-image" : ""}`} style={{ background: r.tint, color: r.ink }}>
        {card.imageAssetId ? <CardArtImage assetId={card.imageAssetId} /> : card.glyph}
      </div>
      <div className="kp-big-card-meta">
        <div>
          <strong>{card.name}</strong>
          <small>{subtitle}</small>
        </div>
        <em style={{ background: r.ink }}>{r.name}</em>
      </div>
    </div>
  );
}

export function CardDetailSheet({ recordId, onClose }: { recordId: string | null; onClose: () => void }) {
  return (
    <Sheet open={!!recordId} onClose={onClose} width={560} persistent label="卡片详情">
      {recordId && <CardDetail key={recordId} recordId={recordId} onClose={onClose} />}
    </Sheet>
  );
}

function CardDetail({ recordId, onClose }: { recordId: string; onClose: () => void }) {
  const cal = useCalendar();
  const record = useStore((s) => s.childCards[recordId]);
  const card = useStore((s) => (record ? s.cards[record.cardId] : undefined));
  const series = useStore((s) => (card ? s.cardSeries[card.seriesId] : undefined));
  const rule = useStore((s) => (record?.sourceType === "achievement" && record.sourceId ? s.achievementRules[record.sourceId] : undefined));
  const ck = useStore((s) => (record?.sourceType === "checkin" && record.sourceId ? s.checkins[record.sourceId] : undefined));
  const [baseline, setBaseline] = useState(record);
  const [story, setStory] = useState(record?.story ?? "");
  const [date, setDate] = useState(record?.occurredOn ?? cal.today());
  const [photos, setPhotos] = useState<string[]>(record?.photoAssetIds ?? []);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  // 还没改过时跟着同步更新
  useEffect(() => {
    if (!record || !baseline || record.seq === baseline.seq) return;
    const edited = story !== (baseline.story ?? "") || date !== (baseline.occurredOn ?? date) || JSON.stringify(photos) !== JSON.stringify(baseline.photoAssetIds ?? []);
    if (edited) return;
    setStory(record.story ?? "");
    setDate(record.occurredOn ?? date);
    setPhotos(record.photoAssetIds ?? []);
    setBaseline(record);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.seq]);

  if (!record || !card) return null;
  const changed = story.trim() !== (record.story ?? "") || date !== (record.occurredOn ?? date) || JSON.stringify(photos) !== JSON.stringify(record.photoAssetIds ?? []);
  const subtitle = series?.kind === "story" ? `成长故事 · 第 ${card.no} 张` : `${series?.name ?? ""} · No.${String(card.no).padStart(2, "0")}`;
  const meta = [`发卡于 ${cal.relativeDay(cal.dateString(record.obtainedAt))}`, record.actorName ? `${record.actorName}发的` : null].filter(Boolean).join(" · ");

  const save = async () => {
    setBusy(true);
    const s = story.trim();
    const r = await run(() => writeEntity("childCards", "PATCH", `child-cards/${record.id}`, { story: s || null, occurredOn: date, photoAssetIds: photos }), "已保存");
    setBusy(false);
    if (r) onClose();
  };

  return (
    <>
      <ModalHeader title={card.name} actionTitle="保存" actionEnabled={changed && !uploading} busy={busy} onCancel={onClose} onAction={() => void save()} />
      <SheetBody gap={12}>
        <BigCard card={card} subtitle={subtitle} />
        <div className="kp-record-meta">
          <small>{meta}</small>
          {!!record.points && <strong>随卡加了 {record.points} 分</strong>}
          {rule && <small>对应成就：{rule.name}</small>}
          {ck && <small>完成了：{ck.title}</small>}
        </div>
        <FormCard>
          <div className="kp-grant-block">
            <TextArea label="发生了什么" value={story} onChange={(v) => setStory(v.slice(0, 500))} placeholder="写下这件事" rows={3} />
          </div>
          <RowDivider />
          <ValueRow title="哪天的事">
            <input type="date" className="kp-grant-date" value={date} max={cal.today()} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </ValueRow>
        </FormCard>
        <FormCard>
          <div className="kp-grant-block">
            <CardPhotosEditor value={photos} onChange={setPhotos} onUploading={setUploading} onView={setViewing} />
          </div>
        </FormCard>
      </SheetBody>
      {viewing && (
        <button type="button" className="kp-photo-viewer" onClick={() => setViewing(null)} aria-label="关闭大图">
          <CardPhoto assetId={viewing} />
        </button>
      )}
    </>
  );
}
